"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
export default function CallToAction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3422" id="call-to-action" className="bg-background text-foreground relative min-h-screen w-full overflow-hidden flex items-center justify-center px-6 py-20">
      {}
      <div className="absolute inset-0 bg-cover bg-center blur-2xl opacity-25 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_cnc_controller_bg.png)]" aria-hidden="true" />

      {}
      <div className="absolute inset-0 opacity-[0.12] pointer-events-none" aria-hidden="true" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "48px 48px"
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
      background: "radial-gradient(circle at 85% 90%, var(--chart-3) 0%, transparent 55%)",
      opacity: 0.35
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none mix-blend-screen opacity-20" aria-hidden="true" style={{
      background: "conic-gradient(from 0deg at 50% 50%, transparent, var(--chart-1), transparent, var(--chart-3), transparent)",
      animation: "luxe-rotate 60s linear infinite"
    }} />

      <style>{`
        @keyframes luxe-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes luxe-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
        @keyframes luxe-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>

      {}
      <div className="absolute top-6 left-6 md:top-10 md:left-10 pointer-events-none" aria-hidden="true">
        <div className="w-12 h-12 md:w-20 md:h-20 border-l-2 border-t-2 border-chart-3/40" />
      </div>
      <div className="absolute top-6 right-6 md:top-10 md:right-10 pointer-events-none" aria-hidden="true">
        <div className="w-12 h-12 md:w-20 md:h-20 border-r-2 border-t-2 border-chart-3/40" />
      </div>
      <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 pointer-events-none" aria-hidden="true">
        <div className="w-12 h-12 md:w-20 md:h-20 border-l-2 border-b-2 border-chart-3/40" />
      </div>
      <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 pointer-events-none" aria-hidden="true">
        <div className="w-12 h-12 md:w-20 md:h-20 border-r-2 border-b-2 border-chart-3/40" />
      </div>

      {}
      <div ref={ref} className={`relative z-10 max-w-6xl mx-auto text-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="flex items-center justify-center gap-3 mb-10">
          
          
        </div>

        {}
        <h2 className="font-default font-bold text-foreground leading-[0.95] tracking-tighter text-[64px] sm:text-[96px] md:text-[140px] lg:text-[180px] xl:text-[200px]">
          <span className="block">Optimize your</span>
          <span className="block text-chart-3">system.</span>
        </h2>

        {}
        <p className="mt-10 md:mt-12 mx-auto max-w-2xl text-base md:text-xl text-muted-foreground font-default">Schedule an onsite appointment for CNC retrofitting or source Siemens spare parts today.</p>

        {}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">
          <Link to="/ContactUsPage" aria-label="Contact an expert">
            <Button size="lg" className="group relative h-14 px-8 bg-chart-3 text-primary-foreground hover:bg-chart-3/90 font-mono text-xs md:text-sm uppercase tracking-[0.2em] rounded-md shadow-md">
              {}
              <span className="absolute top-1 left-1 w-2.5 h-2.5 border-l border-t border-primary-foreground/0 group-hover:border-primary-foreground transition-colors duration-300" aria-hidden="true" />
              <span className="absolute top-1 right-1 w-2.5 h-2.5 border-r border-t border-primary-foreground/0 group-hover:border-primary-foreground transition-colors duration-300" aria-hidden="true" />
              <span className="absolute bottom-1 left-1 w-2.5 h-2.5 border-l border-b border-primary-foreground/0 group-hover:border-primary-foreground transition-colors duration-300" aria-hidden="true" />
              <span className="absolute bottom-1 right-1 w-2.5 h-2.5 border-r border-b border-primary-foreground/0 group-hover:border-primary-foreground transition-colors duration-300" aria-hidden="true" />
              Contact an Expert
              <ArrowRight className="ml-1 size-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>

          <Link to="/PartsCataloguePage" aria-label="Browse Siemens parts">
            <Button size="lg" variant="outline" className="h-14 px-8 bg-transparent border border-chart-3 text-chart-3 hover:bg-chart-3 hover:text-primary-foreground font-mono text-xs md:text-sm uppercase tracking-[0.2em] rounded-md transition-colors duration-300">
              Browse Siemens Parts
            </Button>
          </Link>
        </div>

        {}
        <div className="mt-12 flex items-center justify-center gap-2.5">
          <span className="w-1.5 h-1.5 rounded-md bg-chart-3" style={{
          animation: "luxe-pulse 1.8s ease-in-out infinite"
        }} aria-hidden="true" />
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Technical Support
          </span>
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-muted-foreground/60">
            /
          </span>
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-foreground">
            24/7 Available
          </span>
        </div>
      </div>
    </section>;
}